import {
  isFeatureHiddenInPlanCards,
  isInboxPlanVersionSmallerThan18,
} from 'ducks/billing/util'
import { capitalize, pluralize } from 'util/strings'
import {
  FEATURE_INBOX_API_ACCESS,
  FEATURE_INBOX_CANNED_REPLIES_TIER_1,
  FEATURE_INBOX_CUSTOM_FIELDS,
  FEATURE_INBOX_CUSTOM_PROFILE,
  FEATURE_INBOX_EXPORTS,
  FEATURE_INBOX_MAX_CANNED_REPLIES,
  FEATURE_INBOX_MAX_CHANNELS,
  FEATURE_INBOX_MAX_FOLDERS,
  FEATURE_INBOX_MAX_INTEGRATIONS,
  FEATURE_INBOX_MAX_MAILBOXES,
  FEATURE_INBOX_MAX_REPORTING_DAYS,
  FEATURE_INBOX_MAX_RULES,
  FEATURE_INBOX_MAX_USERS,
  FEATURE_INBOX_MAX_VIEWER_USERS,
  FEATURE_INBOX_MAX_WIDGETS,
  FEATURE_INBOX_PREMIUM_INTEGRATIONS,
  FEATURE_INBOX_RULES_TIER_1,
  FEATURE_INBOX_RULES_TIER_2,
  FEATURE_INBOX_SATISFACTIONS_RATINGS,
  FEATURE_INBOX_TEAMS,
  FEATURE_KB_BRANDING,
  FEATURE_KB_CUSTOM_SCRIPTS,
  FEATURE_KB_MAX_ARTICLES,
  FEATURE_KB_MAX_KBS,
  FEATURE_KB_PRIVATE,
  FEATURE_KB_SEO,
  FEATURE_ROUND_ROBIN,
} from 'ducks/billing/featureTypes'

export const normalizeDays = count => {
  if (count === 'unlimited') return capitalize(count)
  const months = parseInt(count / 30, 10)
  if (months <= 1) {
    return `${count} ${pluralize(count, 'day')}`
  }
  return `${months} ${pluralize(months, 'month')}`
}

const planVersionsWithMinAgents = ['v2', 'v3', 'v14']

export const getFeatureTitles = ({
  isKb,
  showKbPlans,
  versions,
  pricings,
} = {}) => {
  const showNewFeatures = versions.some(
    version => !isInboxPlanVersionSmallerThan18(version)
  )
  const showMinAgents = versions.some(version =>
    planVersionsWithMinAgents.includes(version)
  )
  if (isKb) {
    const KbFeatures = [
      {
        name: 'Knowledge Bases',
        key: FEATURE_KB_MAX_KBS,
      },
      {
        name: 'Articles',
        key: FEATURE_KB_MAX_ARTICLES,
      },
      {
        name: 'Reporting History',
        key: FEATURE_INBOX_MAX_REPORTING_DAYS,
        normalizer: normalizeDays,
      },
      {
        name: 'SEO settings',
        key: FEATURE_KB_SEO,
      },
      {
        name: 'Custom Scripts',
        key: FEATURE_KB_CUSTOM_SCRIPTS,
      },
      {
        name: 'Access Control',
        key: FEATURE_KB_PRIVATE,
      },
      {
        name: 'Remove Groove branding',
        key: FEATURE_KB_BRANDING,
      },
    ]
    return {
      header: {
        key: 'header',
        list: KbFeatures,
      },
    }
  }
  const headerGroup = [
    {
      name: `Minimum ${app.t('agent')}`,
      key: 'minimumQuantity',
      hidden: !showMinAgents,
    },
    {
      name: `Maximum ${app.t('Agents')}`,
      key: FEATURE_INBOX_MAX_USERS,
    },
    {
      name: 'Lite users',
      key: FEATURE_INBOX_MAX_VIEWER_USERS,
      hidden: isFeatureHiddenInPlanCards(
        pricings,
        FEATURE_INBOX_MAX_VIEWER_USERS
      ),
    },
  ]

  const productsGroup = [
    {
      name: !showNewFeatures
        ? app.t('Mailboxes')
        : 'Shared inboxes with email, live chat, and social channels',
      key: !showNewFeatures
        ? FEATURE_INBOX_MAX_MAILBOXES
        : FEATURE_INBOX_MAX_CHANNELS,
    },
    {
      name: 'Knowledge Bases',
      key: FEATURE_KB_MAX_KBS,
      hidden: !showNewFeatures && showKbPlans,
    },
    {
      name: 'Help Widgets',
      key: FEATURE_INBOX_MAX_WIDGETS,
      hidden: !showNewFeatures,
    },
    {
      name: 'Reporting History',
      key: FEATURE_INBOX_MAX_REPORTING_DAYS,
      normalizer: normalizeDays,
    },
  ]

  const inboxGroup = [
    {
      name: 'Teams',
      key: FEATURE_INBOX_TEAMS,
    },
    {
      name: 'Satisfaction Ratings',
      key: FEATURE_INBOX_SATISFACTIONS_RATINGS,
    },
    {
      name: app.t('Canned_Replies'),
      key: FEATURE_INBOX_MAX_CANNED_REPLIES,
    },
    {
      name: `${app.t('Canned_Reply')} Actions`,
      key: FEATURE_INBOX_CANNED_REPLIES_TIER_1,
      tooltip: `This feature allows you to add actions when using an ${app.t(
        'Canned_Reply'
      )}, such as tagging, assigning or snoozing a conversation`,
    },
    {
      name: `${app.t('Folders')}`,
      key: FEATURE_INBOX_MAX_FOLDERS,
    },
    {
      name: 'Rules',
      key: FEATURE_INBOX_MAX_RULES,
    },
    {
      name: 'Advanced Rules',
      key: FEATURE_INBOX_RULES_TIER_1,
      tooltip:
        'Get even more out of rules with additional firing triggers, conditions and actions',
    },
    {
      name: 'Time Based Rules',
      key: FEATURE_INBOX_RULES_TIER_2,
    },
    {
      name: 'Round Robin with Load balancing',
      key: FEATURE_ROUND_ROBIN,
      tooltip: `Automatically assign conversations to the ${app.t(
        'agents'
      )} that have the most availability`,
    },
    {
      name: 'Integrations',
      key: FEATURE_INBOX_MAX_INTEGRATIONS,
    },
    {
      name: 'Premium integrations',
      key: FEATURE_INBOX_PREMIUM_INTEGRATIONS,
    },
  ]

  const advancedGroup = [
    {
      name: `Custom Profile`,
      key: FEATURE_INBOX_CUSTOM_PROFILE,
    },
    {
      name: `Custom Fields`,
      key: FEATURE_INBOX_CUSTOM_FIELDS,
    },
    {
      name: `API Access`,
      key: FEATURE_INBOX_API_ACCESS,
    },
    {
      name: `Data Export`,
      key: FEATURE_INBOX_EXPORTS,
    },
  ]

  return {
    header: {
      key: 'header',
      list: headerGroup.filter(item => !item.hidden),
    },
    products: {
      title: 'Products',
      key: 'products',
      list: productsGroup.filter(item => !item.hidden),
    },
    inbox: {
      title: `${app.t('Mailbox')} features`,
      key: 'inbox',
      list: inboxGroup.filter(item => !item.hidden),
    },
    advanced: {
      title: 'Advanced',
      key: 'advanced',
      list: advancedGroup.filter(item => !item.hidden),
    },
  }
}
